import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { feedbackAPI } from "@/lib/api";
import { MessageCircle, Send, X, Bug, Lightbulb, Heart, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "general", label: "General", icon: MessageSquare, color: "#003087" },
  { value: "bug", label: "Bug", icon: Bug, color: "#D5281B" },
  { value: "feature", label: "Idea", icon: Lightbulb, color: "#FFB81C" },
  { value: "praise", label: "Praise", icon: Heart, color: "#007F3B" },
];

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("general");
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error("Please enter your feedback");
      return;
    }
    setSubmitting(true);
    try {
      await feedbackAPI.submit({
        type,
        message: message.trim(),
        page: location.pathname,
        rating,
      });
      toast.success("Thank you for your feedback!");
      setOpen(false);
      setMessage("");
      setType("general");
      setRating(null);
    } catch {
      toast.error("Failed to send feedback");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-6 z-50 w-12 h-12 rounded-full bg-[#003087] text-white shadow-lg hover:bg-[#002366] hover:scale-105 transition-all duration-200 flex items-center justify-center group"
        data-testid="feedback-fab"
        aria-label="Send feedback"
      >
        <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
      </button>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#003087]" style={{ fontFamily: "Public Sans" }}>Send Feedback</DialogTitle>
            <DialogDescription>Help us improve BandBoost AI. Your input shapes what we build next.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            {/* Type selector */}
            <div className="flex gap-2" data-testid="feedback-type-selector">
              {TYPES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setType(t.value)}
                  className={`flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg border text-xs font-medium transition-all ${
                    type === t.value
                      ? "border-[#003087] bg-[#003087]/5 text-[#003087]"
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid={`feedback-type-${t.value}`}
                >
                  <t.icon className="w-4 h-4" style={{ color: type === t.value ? t.color : undefined }} />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Message */}
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">Your feedback</Label>
              <Textarea
                placeholder={
                  type === "bug" ? "Describe the issue you encountered..." :
                  type === "feature" ? "What feature would help your workflow?" :
                  type === "praise" ? "What do you love about BandBoost AI?" :
                  "Tell us what you think..."
                }
                className="min-h-[100px] resize-none"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="feedback-message"
              />
            </div>

            {/* Quick rating */}
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-700">How useful is BandBoost AI? (optional)</Label>
              <div className="flex gap-1.5" data-testid="feedback-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setRating(rating === n ? null : n)}
                    className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                      rating === n
                        ? "bg-[#003087] text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                    data-testid={`feedback-rating-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <Button
              className="w-full bg-[#003087] text-white hover:bg-[#002366]"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="feedback-submit-btn"
            >
              <Send className="w-4 h-4 mr-2" /> {submitting ? "Sending..." : "Send Feedback"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
